import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import toast from "react-hot-toast";
import jwt_decode from "jwt-decode";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Label } from "../../Components/ui/label";
import { FourDots } from "../../Components/FourDots";
import { AuthFooter } from "../../Components/AuthFooter";
import measureLogo from "/images/7bef2f4973b64f555e9540afb88ba765fa41f030.png";
import TermsAndConditionsModal from "../../Components/Modal/TermsAndConditionsModal";
import * as Sentry from "@sentry/react";

interface SignUpProps {}

export default function SignUp({}: SignUpProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    confirmTerm: false,
    confirmAdultAge: false,
  });

  const [passwordFocused, setPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [decodedToken, setDecodedToken] = useState<any>();
  const [token, setToken] = useState<string>();
  const [termsAndConditionsModalVisible, setTermsAndConditionsModalVisible] = useState(false);

  useEffect(() => {
    const localToken = new URLSearchParams(location.search).get("token");
    if (localToken) {
      setToken(localToken);
      let localDecodedToken = jwt_decode(localToken);
      setDecodedToken(localDecodedToken);
      if ((localDecodedToken as any).email) {
        setFormData(prev => ({ ...prev, email: (localDecodedToken as any).email }));
      }
    }
  }, [location]);

  // Password validation checks
  const passwordChecks = {
    minLength: formData.password.length >= 8,
    maxLength: formData.password.length <= 30,
    hasNumber: /\d/.test(formData.password),
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password),
  };

  const allPasswordRequirementsMet =
    Object.values(passwordChecks).every(Boolean);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!allPasswordRequirementsMet) {
      toast.error("Please meet all password requirements");
      return;
    }

    if (!formData.confirmTerm) {
      toast.error("Please agree to the terms & conditions");
      return;
    }

    if (!formData.confirmAdultAge) {
      toast.error("Please confirm you are 18 years or older");
      return;
    }

    try {
      setLoading(true);
      let signUpInput = {
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
        },
      };

      if (token) {
        signUpInput.attributes["custom:token"] = token;
      }
      const response = await Auth.signUp(signUpInput);
      if (response.userSub) {
        navigate("/confirm-signup", {
          state: {
            email: formData.email,
            password: formData.password,
          },
        });
      }
    } catch (error: any) {
      Sentry.captureException(error);
      setLoading(false);
      if (error.code === "UsernameExistsException") {
        toast.error("Email already exists");
      } else if (error.code === "UserLambdaValidationException") {
        toast.error("Email already exists");
      } else {
        toast.error("Unable to sign Up");
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Link to="/" className="mb-8">
              <img
                src={measureLogo}
                alt="Measure Ignite"
                className="h-25 w-auto"
              />
            </Link>
          </div>

          <form onSubmit={handleSignUp} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setPasswordFocused(true)}
                required
                maxLength={30}
              />

              {/* Password Requirements */}
              {(passwordFocused || formData.password) && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-1.5 text-xs">
                  <PasswordRequirement
                    met={passwordChecks.minLength}
                    text="Minimum 8 characters"
                  />
                  <PasswordRequirement
                    met={passwordChecks.maxLength}
                    text="30 max"
                  />
                  <PasswordRequirement
                    met={passwordChecks.hasNumber}
                    text="1 number"
                  />
                  <PasswordRequirement
                    met={passwordChecks.hasUppercase}
                    text="1 uppercase letter"
                  />
                  <PasswordRequirement
                    met={passwordChecks.hasLowercase}
                    text="1 lowercase letter"
                  />
                  <PasswordRequirement
                    met={passwordChecks.hasSpecial}
                    text="1 special character"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                maxLength={30}
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirmTerm"
                  name="confirmTerm"
                  checked={formData.confirmTerm}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <Label htmlFor="confirmTerm" className="text-sm font-normal cursor-pointer">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsAndConditionsModalVisible(true);
                    }}
                    className="text-orange-600 hover:text-orange-700 font-semibold underline"
                  >
                    terms & conditions
                  </button>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="confirmAdultAge"
                  name="confirmAdultAge"
                  checked={formData.confirmAdultAge}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <Label htmlFor="confirmAdultAge" className="text-sm font-normal cursor-pointer">
                  I am 18 years old or more
                </Label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-500 hover:bg-orange-600"
              disabled={!allPasswordRequirementsMet || loading}
            >
              {loading ? "Signing Up..." : "Create Account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Already a member?{" "}
            <Link
              to="/login"
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              Sign in
            </Link>
          </p>

          <AuthFooter />
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:block flex-1 bg-gradient-to-br from-orange-100 via-pink-100 to-orange-50 p-12">
        <div className="h-full flex flex-col justify-center items-center">
          <div className="max-w-lg text-center mb-12">
            <h2 className="text-3xl font-semibold mb-4">
              Welcome to Ignite
            </h2>
            <p className="text-gray-700 mb-6">
              Join a community of organizations tracking impact,
              generating reports, and building meaningful
              connections.
            </p>
            <div className="space-y-3 text-left">
              <div className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">✓</span>
                <span className="text-gray-700">
                  Track outcomes and metrics
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">✓</span>
                <span className="text-gray-700">
                  Generate foundation-ready reports
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-orange-500 mt-1">✓</span>
                <span className="text-gray-700">
                  Connect with aligned organizations
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TermsAndConditionsModal
          isOpen={termsAndConditionsModalVisible}
          onClose={() => setTermsAndConditionsModalVisible(false)}
        />
    </div>
  );
}

// Password Requirement Component
function PasswordRequirement({
  met,
  text,
}: {
  met: boolean;
  text: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-4 h-4 rounded-full flex items-center justify-center ${
          met ? "bg-green-500" : "bg-gray-300"
        }`}
      >
        {met && (
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
          >
            <path
              d="M2 5l2 2 4-4"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span
        className={met ? "text-green-700" : "text-gray-600"}
      >
        {text}
      </span>
    </div>
  );
}
